/**
 * Astronomy — pure-JS lunar phase + illumination + named phase.
 *
 * Synodic month = 29.530588853 days. Reference new moon: 2000-01-06 18:14 UTC
 * (Julian Day 2451550.26). Accurate to within ~minutes for displayable
 * phase / percent / phase name — no external API or dependency.
 */

const SYNODIC_MONTH_DAYS = 29.530588853;
const REF_NEW_MOON_JD = 2451550.26;

export interface LunarState {
  /** 0..1 — fraction of the synodic month (0 = new, 0.5 = full). */
  phaseFraction: number;
  /** 0..100 — disc illumination percent. */
  illumination: number;
  /** Coarse named phase. */
  name:
    | 'new_moon'
    | 'waxing_crescent'
    | 'first_quarter'
    | 'waxing_gibbous'
    | 'full_moon'
    | 'waning_gibbous'
    | 'last_quarter'
    | 'waning_crescent';
  /** Emoji that mirrors the named phase. */
  emoji: string;
}

export function getLunarState(date: Date = new Date()): LunarState {
  const jd = toJulianDay(date);
  let phase = ((jd - REF_NEW_MOON_JD) % SYNODIC_MONTH_DAYS) / SYNODIC_MONTH_DAYS;
  if (phase < 0) phase += 1;
  // Illumination follows a (1 - cos(2π·phase)) / 2 curve — symmetric around full.
  const illumination = Math.round((1 - Math.cos(phase * 2 * Math.PI)) * 50);
  return { phaseFraction: phase, illumination, ...nameAndEmoji(phase) };
}

/** i18n key matching the named phase. */
export function lunarLabelKey(state: LunarState): string {
  return `weather_moon_${state.name}`;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function toJulianDay(date: Date): number {
  // Standard Gregorian → JD. Time of day matters for sub-day accuracy.
  return date.getTime() / 86400000 + 2440587.5;
}

function nameAndEmoji(phase: number): { name: LunarState['name']; emoji: string } {
  // 8-band split centered on the cardinal phases (new, Q1, full, Q3) with
  // ~3.7-day windows. Cardinal phases get a tighter window so they don't
  // get swallowed by the gibbous/crescent neighbors.
  if (phase < 0.0625 || phase >= 0.9375) return { name: 'new_moon', emoji: '🌑' };
  if (phase < 0.1875) return { name: 'waxing_crescent', emoji: '🌒' };
  if (phase < 0.3125) return { name: 'first_quarter', emoji: '🌓' };
  if (phase < 0.4375) return { name: 'waxing_gibbous', emoji: '🌔' };
  if (phase < 0.5625) return { name: 'full_moon', emoji: '🌕' };
  if (phase < 0.6875) return { name: 'waning_gibbous', emoji: '🌖' };
  if (phase < 0.8125) return { name: 'last_quarter', emoji: '🌗' };
  return { name: 'waning_crescent', emoji: '🌘' };
}
